import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAppShell } from '../../contexts/AppShellContext';
import { pageVisible, defaultPathFor } from '../../utils/rbac';

export function ProtectedRoute({ children, page }: { children: React.ReactNode; page?: string }) {
  const { isAuthenticated, loading, user } = useAuth();
  const { demoTeam } = useAppShell();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-wash">
        <div className="text-center">
          <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-teal-500 border-r-transparent" />
          <p className="mt-4 text-muted text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Page-level RBAC gate (UX only — the backend enforces the data behind it).
  // demoTeam overrides the effective team_id so the "viewing as" toggle
  // actually gates navigation, not just the sidebar's visible links.
  if (page && !pageVisible(user, page, demoTeam)) {
    return <Navigate to={defaultPathFor(user, demoTeam)} replace />;
  }

  return <>{children}</>;
}

// Per-route page gate used inside the shared AppShell layout route.
export function PageGate({ children, page }: { children: React.ReactNode; page: string }) {
  const { user } = useAuth();
  const { demoTeam } = useAppShell();
  if (!pageVisible(user, page, demoTeam)) {
    return <Navigate to={defaultPathFor(user, demoTeam)} replace />;
  }
  return <>{children}</>;
}

// The "/" landing — sends each binding to its first visible page.
export function DefaultRedirect() {
  const { user, loading } = useAuth();
  const { demoTeam } = useAppShell();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={defaultPathFor(user, demoTeam)} replace />;
}
